import type { AuthSessionSnapshot, AuthUser } from "./types";

export const normalizePositiveInteger = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const normalizeNonNegativeInteger = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

const toNullableString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

export const resolveAuthCompanyId = (
  stateCompanyId: unknown,
  user?: AuthUser | null
): number | null =>
  normalizePositiveInteger(
    stateCompanyId ??
      user?.companyId ??
      user?.companyRefId ??
      user?.CompanyRefid ??
      user?.CompanyId ??
      user?.companyID
  );

export const resolveAuthRoleId = (user?: AuthUser | null): number | null =>
  normalizePositiveInteger(
    user?.roleId ??
    user?.RoleId ??
    user?.RoleID ??
    user?.role_id ??
    user?.role
  );

export const resolveAuthUserId = (user?: AuthUser | null): number | null =>
  normalizePositiveInteger(user?.UserId ?? user?.userId);

export const resolveAuthEmployeeId = (user?: AuthUser | null): number | null =>
  normalizePositiveInteger(
    user?.employeeRefId ??
    user?.EmployeeRefid ??
    user?.EmployeeRefId ??
    user?.employeeId ??
    user?.EmployeeId ??
    user?.UserId ??
    user?.userId
  );

export const resolveAuthRoleName = (user?: AuthUser | null): string | null =>
  toNullableString(user?.rolename ?? user?.roleName ?? user?.RoleName);

export const resolveAuthUserName = (user?: AuthUser | null): string | null =>
  toNullableString(user?.userName ?? user?.UserName ?? user?.EmployeeName ?? user?.employeeName);

export const resolveAuthPermissionId = (user?: AuthUser | null): number | null =>
  normalizeNonNegativeInteger(user?.permisionId ?? user?.permissionId);

export const deriveAuthSession = (
  user: AuthUser | null,
  token: unknown
): AuthSessionSnapshot => ({
  user,
  token: toNullableString(token) ?? toNullableString(user?.token),
  roleName: resolveAuthRoleName(user),
  roleId: resolveAuthRoleId(user),
  userName: resolveAuthUserName(user),
  userId: resolveAuthUserId(user),
  employeeId: resolveAuthEmployeeId(user),
  companyId: resolveAuthCompanyId(undefined, user),
  permissionId: resolveAuthPermissionId(user),
});
